import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';

const TONES = {
  slate: 'text-slate-900', red: 'text-red-600', orange: 'text-orange-600', amber: 'text-amber-600',
  green: 'text-emerald-600', blue: 'text-blue-600', purple: 'text-purple-600', gray: 'text-slate-500', rose: 'text-rose-700',
};

const slug = (s) => s.toLowerCase().replace(/\s+/g, '-');

function MiniStat({ label, value, tone = 'slate', testid, to }) {
  return (
    <Link
      to={to}
      className="block bg-white border border-slate-200 rounded-lg py-3 px-4 transition-[box-shadow,transform,border-color] duration-150 hover:-translate-y-0.5 hover:shadow-md hover:border-slate-300"
      data-testid={`${testid}-card`}
    >
      <div className={`text-2xl font-bold ${TONES[tone]}`} data-testid={testid}>{value ?? 0}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </Link>
  );
}

const q = (base, key, value) => `${base}?${key}=${encodeURIComponent(value)}`;

function StatRow({ title, items, prefix, cols }) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{title}</h3>
      <div className={`grid grid-cols-2 gap-3 ${cols}`}>
        {items.map((i) => <MiniStat key={i.label} {...i} testid={`${prefix}-${slug(i.label)}`} />)}
      </div>
    </div>
  );
}

function Section({ title, to, subtitle, testid, children }) {
  return (
    <div className="border-t-4 border-slate-600 pt-6 mt-12" data-testid={testid}>
      <div className="flex items-center gap-3 mb-6">
        <Link
          to={to}
          className="group inline-flex items-center gap-1.5 px-4 py-2 rounded-sm text-sm font-semibold bg-slate-100 text-slate-800 hover:bg-slate-200 transition-colors"
          data-testid={`${testid}-link`}
        >
          {title}
          <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
        <div className="text-sm text-slate-600">{subtitle}</div>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

export const ActionsDashboardSection = ({ stats }) => (
  <Section title="Improvement & Actions" to="/improvement-actions" subtitle={`${stats.total} total actions tracked`} testid="dash-actions">
    <StatRow
      title="Action Status"
      prefix="dash-actions"
      cols="md:grid-cols-4 lg:grid-cols-7"
      items={[
        { label: 'Total', value: stats.total, to: '/improvement-actions' },
        { label: 'Open', value: stats.open, tone: 'red', to: q('/improvement-actions', 'status', 'Open') },
        { label: 'In Progress', value: stats.inProgress, tone: 'blue', to: q('/improvement-actions', 'status', 'In Progress') },
        { label: 'Pending Review', value: stats.pendingReview, tone: 'amber', to: q('/improvement-actions', 'status', 'Pending Review') },
        { label: 'Overdue', value: stats.overdue, tone: 'rose', to: q('/improvement-actions', 'overdue', '1') },
        { label: 'Completed', value: stats.completed, tone: 'green', to: q('/improvement-actions', 'status', 'Completed') },
        { label: 'Closed', value: stats.closed, tone: 'gray', to: q('/improvement-actions', 'status', 'Closed') },
      ]}
    />
  </Section>
);

const RISK_STATUS_TONES = { Open: 'red', 'In Progress': 'amber', Mitigated: 'blue', Accepted: 'gray', Transferred: 'purple', Closed: 'green' };

export const RiskDashboardSection = ({ stats }) => (
  <Section title="Risk Register" to="/risk-engine" subtitle={`${stats.total} total risks tracked`} testid="dash-risk">
    <StatRow
      title="Risk Rating"
      prefix="dash-risk-rating"
      cols="md:grid-cols-5"
      items={[
        { label: 'Total', value: stats.total, to: '/risk-engine' },
        ...['Critical', 'High', 'Medium', 'Low'].map((r, i) => ({
          label: r, value: stats.byRating[r], tone: ['red', 'orange', 'amber', 'slate'][i], to: q('/risk-engine', 'rating', r),
        })),
      ]}
    />
    <StatRow
      title="Risk Status"
      prefix="dash-risk-status"
      cols="md:grid-cols-6"
      items={Object.entries(RISK_STATUS_TONES).map(([label, tone]) => ({ label, tone, value: stats.byStatus[label], to: q('/risk-engine', 'status', label) }))}
    />
  </Section>
);

const INCIDENT_STATUS_TONES = { Reported: 'red', 'Under Investigation': 'amber', Contained: 'blue', Resolved: 'green', Closed: 'gray' };

export const IncidentDashboardSection = ({ stats }) => (
  <Section title="Incident Register" to="/incident-register" subtitle={`${stats.total} total incidents tracked`} testid="dash-incident">
    <StatRow
      title="Incident Severity"
      prefix="dash-incident-severity"
      cols="md:grid-cols-5"
      items={[
        { label: 'Total', value: stats.total, to: '/incident-register' },
        ...['Critical', 'High', 'Medium', 'Low'].map((sv, i) => ({
          label: sv, value: stats.bySeverity[sv], tone: ['red', 'orange', 'amber', 'green'][i], to: q('/incident-register', 'severity', sv),
        })),
      ]}
    />
    <StatRow
      title="Incident Status"
      prefix="dash-incident-status"
      cols="md:grid-cols-5"
      items={Object.entries(INCIDENT_STATUS_TONES).map(([label, tone]) => ({ label, tone, value: stats.byStatus[label], to: q('/incident-register', 'status', label) }))}
    />
  </Section>
);

export const PolicyDashboardSection = ({ stats }) => (
  <Section title="Security Policies & Procedures" to="/policies" subtitle={`${stats.total} total policies`} testid="dash-policy">
    <StatRow
      title="Policy Status"
      prefix="dash-policy-status"
      cols="md:grid-cols-4"
      items={[
        { label: 'Total', value: stats.total, to: '/policies' },
        { label: 'Draft', value: stats.draft, tone: 'gray', to: q('/policies', 'status', 'Draft') },
        { label: 'Under Review', value: stats.underReview, tone: 'amber', to: q('/policies', 'status', 'Under Review') },
        { label: 'Approved', value: stats.approved, tone: 'green', to: q('/policies', 'status', 'Approved') },
      ]}
    />
    <StatRow
      title="Review Status"
      prefix="dash-policy-review"
      cols="md:grid-cols-3"
      items={[
        { label: 'Overdue Review', value: stats.reviewOverdue, tone: 'red', to: q('/policies', 'review', 'overdue') },
        { label: 'Due Soon', value: stats.reviewDueSoon, tone: 'amber', to: q('/policies', 'review', 'due_soon') },
        { label: 'Current', value: stats.reviewCurrent, tone: 'green', to: q('/policies', 'review', 'current') },
      ]}
    />
    <StatRow
      title="Acknowledgements"
      prefix="dash-policy-ack"
      cols="md:grid-cols-4"
      items={[
        { label: 'Assigned', value: stats.ackTotal, to: '/policy-acknowledgement' },
        { label: 'Acknowledged', value: stats.ackAcknowledged, tone: 'green', to: '/policy-acknowledgement' },
        { label: 'Pending', value: stats.ackPending, tone: 'amber', to: '/policy-acknowledgement' },
        { label: 'Rate', value: `${stats.ackPercent}%`, tone: 'blue', to: '/policy-acknowledgement' },
      ]}
    />
  </Section>
);

export const SupplierDashboardSection = ({ stats }) => (
  <Section title="Supplier Register" to="/supplier-register" subtitle={`${stats.total} total suppliers`} testid="dash-supplier">
    <StatRow
      title="Suppliers"
      prefix="dash-supplier"
      cols="md:grid-cols-5"
      items={[
        { label: 'Total', value: stats.total, to: '/supplier-register' },
        { label: 'Active', value: stats.active, tone: 'green', to: q('/supplier-register', 'card', 'active') },
        { label: 'Critical', value: stats.critical, tone: 'red', to: q('/supplier-register', 'card', 'critical') },
        { label: 'High Risk', value: stats.highRisk, tone: 'orange', to: q('/supplier-register', 'card', 'high_risk') },
        { label: 'Compliant', value: stats.compliant, tone: 'green', to: q('/supplier-register', 'card', 'compliant') },
      ]}
    />
  </Section>
);
